<template>
  <div>
    <!-- Header -->
    <div class="config-section-header">
      <div class="config-section-header__left">
        <v-icon small class="mr-2" color="text_secondary">mdi-timer</v-icon>
        <span class="config-section-label">Configuração da tarja cronômetro</span>
      </div>
    </div>

    <!-- Settings -->
    <div class="sc-settings-list sc-settings-list--with-footer-gap">
      <!-- Position -->
      <div class="sc-settings-list__row">
        <div class="sc-settings-list__info">
          <v-icon small color="text_secondary" class="mr-2">mdi-arrow-left-right</v-icon>
          <span class="sc-settings-list__name">Posição do cronômetro na tarja</span>
        </div>
        <v-select
          outlined dense solo flat hide-details
          v-model="selectedTarjaPosition"
          :items="tarjaPositionOptions"
          placeholder="Selecione"
          class="tc-select"
        />
      </div>

      <!-- Confirm button -->
      <div class="sc-settings-list__row">
        <div class="sc-settings-list__info">
          <v-icon small color="text_secondary" class="mr-2">mdi-gesture-tap</v-icon>
          <div class="sc-settings-list__text">
            <span class="sc-settings-list__name">Botão de confirmação</span>
            <span class="sc-settings-list__desc">Exibir confirmação ao clicar em exibir/remover tarja</span>
          </div>
        </div>
        <v-switch
          v-model="flg_botao_confirmar_exibicao_tarja"
          color="primary" hide-details dense
          class="mt-0 pt-0"
        />
      </div>

      <!-- Show timeout text -->
      <div class="sc-settings-list__row">
        <div class="sc-settings-list__info">
          <v-icon small color="text_secondary" class="mr-2">mdi-text-box</v-icon>
          <div class="sc-settings-list__text">
            <span class="sc-settings-list__name">Exibir texto de tempo esgotado</span>
            <span class="sc-settings-list__desc">Mostrar mensagem quando o cronômetro zerar</span>
          </div>
        </div>
        <v-switch
          v-model="showTimeoutText"
          color="primary" hide-details dense
          class="mt-0 pt-0"
        />
      </div>
    </div>

    <!-- Colors -->
    <div class="tc-colors-header">
      <v-icon x-small class="mr-1" color="text_secondary">mdi-palette</v-icon>
      <span>Cores</span>
    </div>
    <div class="tc-colors-grid">
      <div v-for="color in colorConfigs" :key="color.key">
        <v-menu
          :close-on-content-click="false"
          transition="scale-transition"
          v-model="color.open"
          @input="onMenuToggle(color, $event)"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <div class="action-box color-box" v-bind="attrs" v-on="on">
              <div class="color-preview" :style="{ backgroundColor: getColorHex(color.model) }"></div>
              <span class="action-label">{{ color.label }}</span>
            </div>
          </template>
          <v-card
            class="color-picker-card"
            :style="{ '--picker-color': getColorHex(color.model), '--picker-text': getContrastColor(color.model) }"
          >
            <v-card-text class="pa-3">
              <v-color-picker
                v-model="color.draft"
                dot-size="20"
                swatches-max-height="200"
                hide-mode-switch
                mode="hexa"
              />
              <!-- <div class="color-preview-bar mt-3" :style="{ backgroundColor: color.draft }">
                {{ color.draft }}
              </div> -->
              <div class="color-actions mt-3">
                <v-btn outlined small class="reset-btn" @click="color.draft = color.default">
                  <v-icon small left>mdi-refresh</v-icon>
                  Resetar
                </v-btn>
                <v-btn color="primary" small class="save-btn" @click="confirmColor(color)">
                  OK
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-menu>
      </div>
    </div>

    <!-- Save -->
    <div class="sc-modal__footer">
      <v-btn
        color="primary"
        @click.prevent="salvarConfigTarja"
        :loading="loadingSave"
        class="modal-btn-confirm"
      >
        <v-icon small class="mr-1">mdi-check</v-icon>
        Salvar
      </v-btn>
    </div>
  </div>
</template>

<script>
import { apiClient } from "@/services/api";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "EditarConfiguracaoTarjaCronometro",
  props: {
    cod_producao: Number,
  },
  data() {
    return {
      selectedTarjaPosition: null,
      tarjaPositionOptions: ["Esquerda", "Centro", "Direita"],
      flg_botao_confirmar_exibicao_tarja: null,
      showTimeoutText: null,
      cor_background_chroma_tarja: { hexa: "#00FF00FF" },
      cor_background_card_cronometro: { hexa: "#000000FF" },
      cor_texto_cronometro: { hexa: "#FFFFFFFF" },
      cor_borda_card: { hexa: "#02EDF5FF" },
      cor_texto_last_ten_seconds: { hexa: "#F0E805FF" },
      cor_texto_cronometro_zerado: { hexa: "#F00505FF" },
      loadingSave: false,
      colorConfigs: [
        { model: "cor_background_chroma_tarja", label: "Chroma", key: "chroma", default: "#00FF00FF", open: false, draft: null },
        { model: "cor_background_card_cronometro", label: "Card", key: "card", default: "#000000FF", open: false, draft: null },
        { model: "cor_texto_cronometro", label: "Texto", key: "texto", default: "#FFFFFFFF", open: false, draft: null },
        { model: "cor_borda_card", label: "Borda", key: "borda", default: "#02EDF5FF", open: false, draft: null },
        { model: "cor_texto_last_ten_seconds", label: "10s", key: "10s", default: "#F0E805FF", open: false, draft: null },
        { model: "cor_texto_cronometro_zerado", label: "Zerado", key: "zerado", default: "#F00505FF", open: false, draft: null },
      ],
    };
  },
  async created() {
    await this.getTarjaCronometro({ cod_producao: this.cod_producao });
    if (this.configTarjaCronometro) {
      this.populateConfigTarja();
    }
  },
  computed: {
    ...mapGetters({
      configTarjaCronometro: "tarja_cronometro/getConfigTarja",
    }),
  },
  methods: {
    ...mapActions({
      getTarjaCronometro: "tarja_cronometro/getConfigTarjaCronometro",
    }),
    hexFromColorObj(val) {
      if (!val) return "#000000";
      return typeof val === "object" ? val.hex || val.hexa : val;
    },
    onMenuToggle(color, isOpen) {
      if (isOpen) {
        color.draft = this.extractHex(this.$data[color.model]);
      }
    },
    confirmColor(color) {
      this.$data[color.model] = { hexa: this.extractHex(color.draft) };
      color.open = false;
    },
    getContrastColor(modelName) {
      const hex = this.getColorHex(modelName).replace('#', '');
      // handle 8-digit hexa by trimming alpha
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.55 ? '#000000' : '#FFFFFF';
    },
    getColorHex(modelName) {
      const val = this.$data[modelName];
      if (!val) return "#000000";
      return typeof val === "object" ? val.hex || val.hexa : val;
    },
    extractHex(val) {
      return typeof val === "object" ? val.hexa : val;
    },
    populateConfigTarja() {
      const config = this.$store.state.tarja_cronometro.configTarjaCronometro;
      if (!config) return;
      this.selectedTarjaPosition =
        config.dsc_posicao_tarja.charAt(0).toUpperCase() + config.dsc_posicao_tarja.slice(1);
      this.flg_botao_confirmar_exibicao_tarja = config.flg_botao_confirmar_exibicao;
      this.showTimeoutText = config.flg_show_timeout_text;
      this.cor_background_chroma_tarja = { hexa: config.dsc_hex_background_chroma || "#00FF00FF" };
      this.cor_background_card_cronometro = { hexa: config.dsc_hex_background_card || "#000000FF" };
      this.cor_texto_cronometro = { hexa: config.dsc_hex_cor_texto || "#FFFFFFFF" };
      this.cor_borda_card = { hexa: config.dsc_hex_cor_borda_card || "#02EDF5FF" };
      this.cor_texto_last_ten_seconds = { hexa: config.dsc_hex_cor_texto_last_ten_seconds || "#F0E805FF" };
      this.cor_texto_cronometro_zerado = { hexa: config.dsc_hex_cor_texto_cronometro_zerado || "#F00505FF" };
    },
    async salvarConfigTarja() {
      if (!this.selectedTarjaPosition) {
        this.$toast.info("Selecione a posição da tarja");
        return;
      }
      this.loadingSave = true;
      try {
        const payload = {
          dsc_posicao_tarja: this.selectedTarjaPosition.toLowerCase(),
          flg_botao_confirmar_exibicao: this.flg_botao_confirmar_exibicao_tarja ?? false,
          dsc_hex_background_chroma: this.extractHex(this.cor_background_chroma_tarja),
          dsc_hex_background_card: this.extractHex(this.cor_background_card_cronometro),
          dsc_hex_cor_texto: this.extractHex(this.cor_texto_cronometro),
          dsc_hex_cor_borda_card: this.extractHex(this.cor_borda_card),
          dsc_hex_cor_texto_last_ten_seconds: this.extractHex(this.cor_texto_last_ten_seconds),
          dsc_hex_cor_texto_cronometro_zerado: this.extractHex(this.cor_texto_cronometro_zerado),
          flg_show_timeout_text: this.showTimeoutText,
        };

        if (!this.configTarjaCronometro) {
          await apiClient.post(`createProducaoConfigTarjaCronometro/${this.cod_producao}`, payload);
          this.$toast.success("Configuração criada com sucesso.");
        } else {
          await apiClient.put(
            `updateProducaoConfigTarjaCronometro/${this.configTarjaCronometro.id}/${this.cod_producao}`,
            payload
          );
          const configTarja = this.$store.state.producoes.selectedProducao?.config_tarja;
          if (configTarja) {
            await apiClient.patch("updateCorBackgroundTarja", {
              id_config: configTarja.id_config_tarja,
              cod_producao: this.cod_producao,
              dsc_hex_background_chroma: this.extractHex(this.cor_background_chroma_tarja),
            });
          }
          this.$toast.success("Configuração atualizada com sucesso.");
        }

        await this.getTarjaCronometro({ cod_producao: this.cod_producao });
        this.populateConfigTarja();
      } catch (error) {
        this.$toast.error("Erro ao salvar configuração.");
      } finally {
        this.loadingSave = false;
      }
    },
    
  },
  watch: {
    "$store.state.producoes.selectedProducao": async function (newProducao) {
      if (!newProducao) return;
      await this.getTarjaCronometro({ cod_producao: newProducao.cod_producao });
      if (this.configTarjaCronometro) {
        this.populateConfigTarja();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .color-picker-card .v-color-picker__input input {
  background-color: var(--picker-color) !important;
  color: var(--picker-text) !important;
  border-radius: var(--sc-radius-sm);
  font-size: 0.8rem !important;
  font-weight: 500 !important;
  font-family: "Globotipo" !important;
  padding: 4px 8px;
  transition: background-color 0.15s ease, color 0.15s ease;
}

::v-deep .color-picker-card .v-color-picker__input input + span {
  display: none;
}

.tc-select {
  max-width: 200px;
  flex-shrink: 0;
}

/* Colors section */
.tc-colors-header {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--v-text_secondary-base);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
}

.tc-colors-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
}


.color-box { border-style: solid; }

.color-preview {
  width: 28px;
  height: 28px;
  border-radius: var(--sc-radius-sm);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.color-picker-card { border-radius: var(--sc-radius-lg) !important; }

.color-preview-bar {
  height: 32px;
  border-radius: var(--sc-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.8rem;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.color-actions {
  display: flex;
  gap: 8px;
  .reset-btn { flex: 1; text-transform: none; letter-spacing: 0; }
  .save-btn { flex: 1; text-transform: none; letter-spacing: 0; }
}

/* Switch overrides */
::v-deep .v-input--switch { margin: 0; padding: 0; }
::v-deep .v-input--switch__track { opacity: 0.3; }
</style>
